import { Box, Center, Stack, useCheckboxGroup } from '@chakra-ui/react'
import type { FC } from 'react'
import { useEffect } from 'react'
import { useCallback, useMemo } from 'react'

import StrategyListActions from './components/strategyListActions'
import StrategyName from './components/strategyName'

import { Checkbox } from '@app/components/ui/checkbox'
import { EmptyState } from '@app/components/ui/empty-state'
import useStoreCurrentUser from '@app/hooks/useStoreCurrentUser'
import { useListFilterContext } from '@app/pages/shared/listFilterContext'
import MultiOps from '@app/pages/shared/multiOps'
import SearchRow from '@app/pages/shared/searchRow'
import SelectAllCheckboxHeader from '@app/pages/shared/selectAllCheckboxHeader'
import useGenerateSelectableTableRowFormatter from '@app/pages/shared/useGenerateSelectableTableRowFormatter'
import { abilitiesFor } from '@app/shared/authorization/caslProvider'
import Pagination from '@app/shared/pagination'
import Table from '@app/shared/table'
import SortHeader from '@app/shared/table/components/sortHeader'
import { SortHeaderContextProvider } from '@app/shared/table/contexts/sortHeaderContext'
import type { Column } from '@app/shared/table/types'
import type { PageMetadata } from '@app/store/types'
import type { DomainStrategy } from '@app/types'

type Strategy = DomainStrategy

type MakeColumns = (args: {
  getCheckboxProps: ReturnType<typeof useCheckboxGroup>['getItemProps']
  selected: ReturnType<typeof useCheckboxGroup>['value']
  setSelected: ReturnType<typeof useCheckboxGroup>['setValue']
  formatDateTimeInUserTimeZone: ReturnType<typeof useStoreCurrentUser>['formatDateTimeInUserTimeZone']
  user: ReturnType<typeof useStoreCurrentUser>['user']
}) => Column<Strategy>[]

const makeColumns: MakeColumns = ({
  getCheckboxProps,
  selected,
  setSelected,
  formatDateTimeInUserTimeZone,
  user
}): Column<DomainStrategy>[] => {
  const abilities = abilitiesFor(user)
  const columns: Column<DomainStrategy>[] = []

  if (abilities.can('update', 'strategy')) {
    columns.push({
      header: <SelectAllCheckboxHeader collectionType="strategy" selected={selected} setSelected={setSelected} />,
      width: '5%',
      key: 'checkbox',
      verticalAlign: 'top',
      cell: ({ data: strategy }) => (
        <Center>
          <Checkbox {...getCheckboxProps({ value: strategy.id })} />
        </Center>
      )
    })
  }

  columns.push(
    {
      header: <SortHeader field="name" title="Name" emptyState={false} />,
      key: 'name',
      width: '40%',
      cell: StrategyName
    },
    {
      header: <SortHeader field="createdAt" title="Created" emptyState={false} />,
      key: 'createdAt',
      cell: ({ data }) => formatDateTimeInUserTimeZone(data.createdAt, 'L/d/yyyy')
    },
    {
      header: '',
      cell: function StrategyActions({ data: strategy }) {
        return <StrategyListActions strategy={strategy} />
      }
    }
  )

  return columns
}

type Props = {
  collection: DomainStrategy[]
  metadata: PageMetadata
  movable?: boolean
}

type PopulatedProps = Props & {
  selected: ReturnType<typeof useCheckboxGroup>['value']
  setSelected: ReturnType<typeof useCheckboxGroup>['setValue']
  getCheckboxProps: ReturnType<typeof useCheckboxGroup>['getItemProps']
}

const Empty = () => <EmptyState title="No maps" description="Build a map with AI to get started." />

const Populated: FC<PopulatedProps> = ({ collection, metadata, selected, setSelected, getCheckboxProps }) => {
  const { formatDateTimeInUserTimeZone, user } = useStoreCurrentUser()

  const columns = useMemo(
    () => makeColumns({ getCheckboxProps, selected, setSelected, formatDateTimeInUserTimeZone, user }),
    [formatDateTimeInUserTimeZone, getCheckboxProps, selected, setSelected, user]
  )
  const rowFormatter = useGenerateSelectableTableRowFormatter<DomainStrategy>('strategy', selected, setSelected)

  return (
    <Box overflow="auto">
      <SortHeaderContextProvider defaultState={{ name: 'asc' }}>
        <Table columns={columns} data={collection} rowFormatter={rowFormatter} />
      </SortHeaderContextProvider>
      <Pagination px={6} py={4} {...{ ...metadata }} scrollContainer="main" />
    </Box>
  )
}

const List: FC<Props> = ({ collection, metadata, movable = true }) => {
  const {
    value: selected,
    getItemProps: getCheckboxProps, // TODO: This might not be correct, start here for bugs
    setValue: setSelected
  } = useCheckboxGroup({
    defaultValue: []
  })

  const { page, limitValue, totalCount } = metadata || {}

  const onBulkDelete = useCallback(() => {
    setSelected([])
  }, [setSelected])

  const { listFilter } = useListFilterContext()

  useEffect(() => {
    setSelected([])
  }, [listFilter, setSelected])

  return (
    <Stack gap="0" pb={2}>
      <SearchRow
        leftSummarySibling={
          <MultiOps type="strategy" selected={selected} fontSize="sm" onDelete={onBulkDelete} movable={movable} />
        }
        page={page}
        limitValue={limitValue}
        totalCount={totalCount}
      />
      {collection.length === 0 ? (
        <Empty />
      ) : (
        <Populated
          collection={collection}
          metadata={metadata}
          movable={movable}
          selected={selected}
          setSelected={setSelected}
          getCheckboxProps={getCheckboxProps}
        />
      )}
    </Stack>
  )
}

export default List
